import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, toRefs, withDefaults } from 'vue';
import type { Ages, Audience, MaterialV1, NewMaterialV1 } from '@ilteducation/content-registry-types';
import type { Language } from '@ilteducation/content-ref-data-types';
import { BaseState } from '@ilteducation/content-registry-types/enums/content-enums';
import { cloneDeep, isArray } from 'lodash';
import ChooseAges from '@/components/book/ChooseAges.vue';
import ContentLinks from '@/components/material/ContentLinks.vue';
import ContentRefDataTags from '@/components/material/ContentRefDataTags.vue';
import { getAllRefData } from '@/data-provider/refDataProvider';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import { defaultRealms } from '@/foundations/helpers/defaultRealms';
import RichTextInput from '@/components/RichTextInput.vue';
import type { MaterialPreviewProps, MaterialWithTmpFile, Properties } from './types';
import ComboBox from '@/components/base/ComboBox.vue';
import useMaterial from './use-material';
import useMaterialUtil from './use-material-util';
import parseMaterialFileName from './parse-material-file-name';
import { createBulk, getOriginalTitleRelated, updateBulk } from '@/data-provider/materials';
import DropFile from '@/components/base/DropFile.vue';
import eventBus from '@/foundations/services/eventBus';
import ImportButton from '@/components/buttons/ImportButton.vue';
import PreviewImageDialog from '@/components/material/PreviewImageDialog.vue';
import useUploadMedia from '@/comp/use-upload-media';
import FileInfoBtn from '@/components/material/FileInfoBtn.vue';
import GeneratePreviewDialog from '@/components/material/GeneratePreviewDialog.vue';
import MarketSelect from '@/components/material/MarketSelect.vue';
import ActionsMenu from '@/components/material/ActionsMenu.vue';
import OriginalTitle from '@/components/material/OriginalTitle.vue';
import { useRouter } from 'vue-router/composables';
import RefDataLinks from '@/components/material/RefDataLinks.vue';
import ContentToCollection from '@/components/collections/ContentToCollection.vue';
import { isManabuaSubscription } from '@/foundations/helpers/refDataFilters';

type Emits = {
  (e: 'update', update: NewMaterialV1): void;
};
type Props = {
  id?: string;
  title: string;
  item?: MaterialV1 | NewMaterialV1;
  redirect?: 'create' | 'edit';
  isCreate?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    id: null,
    title: null,
    item: null,
    redirect: { default: 'edit' },
    isCreate: { type: Boolean, default: false }
  },
  emits: ["update"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'update', update: NewMaterialV1): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { id?: string, title: string, item?: MaterialV1 | NewMaterialV1, redirect: 'create' | 'edit', isCreate: boolean }




const { id, item } = toRefs(props);

const router = useRouter();
const { parseMetaData, newMaterial, patchMaterial, languageRealm } = useMaterialUtil();
const { getStateColor, getStateIcon, uploadMedia, toggleMediaState, isStateInProgress, isUploadInProgress } =
  useMaterial();
const { upload } = useUploadMedia();

// form related
const form = ref();
const isFormValid = ref(false);
const selected = ref<MaterialV1[]>([]);
const expanded = ref([]);
const saveInProgress = ref(false);
const itemLoading = ref<Record<string, boolean>>({});

// Upload file related
const guessRealm = ref<boolean>(true);
const emptyStateless = ref<boolean>(false);

// Global attributes for material
const originalTitle = ref<string | undefined>(item.value.originalTitle);
const contentLinks = ref(item.value.contentLinks);
const refdataLinks = ref(item.value.refdataLinks);
const categories = ref(item.value.categories);
const areas = ref(item.value.areas);
const ages = ref<Ages>(item.value.ages);
const roles = ref<string | undefined>(item.value.accessRules.roles || []);
const properties = ref<Properties>(item.value.properties);
const combinedRealms = ref<string[][]>([
  item?.value?.realm
    ? [item.value.realm, ...(item?.value?.availabilityRealms.filter((r) => r !== item.value?.realm) || [])] // Combine realm and availabilityRealms
    : [], // Use only availabilityRealms if realm doesn't exist
]);

const originalItems = ref<MaterialWithTmpFile[]>([cloneDeep(item?.value)]);
const items = ref<MaterialWithTmpFile[]>([item?.value]);

const headers = computed(() => [
  { text: 'ID', value: 'id', width: '50px' },
  { text: 'State', value: 'state', width: '50px' },
  { text: 'Type', value: 'resourceType', width: '50px' },
  { text: 'Preview', value: 'preview', width: '80px' },
  { text: 'Language', value: 'language', width: '10%' },
  { text: 'Title', value: 'title' },
  { text: 'Subscription Type', value: 'subscriptionType', width: '15%' },
  { text: 'Markets', value: 'markets', width: '15%' },
  { text: 'Extra', align: 'center', value: 'data-table-expand', width: '50px' },
  { value: 'actions', width: '50px' },
]);

// Collection attachment operates on the ticked rows only — the same selection the
// Publish/Unpublish buttons use. Rows that have not been saved yet have no id to attach.
const selectedIds = computed<string[]>(() => selected.value.filter((m) => m.id).map((m) => m.id));
const selectedSubscriptions = computed<string[]>(() => [
  ...new Set(selected.value.flatMap((m) => m.accessRules?.subscriptionType || [])),
]);
const selectedAvailabilityRealms = computed<string[]>(() => [
  ...new Set(selected.value.flatMap((m) => m.availabilityRealms || [])),
]);

// The ref-data cards write global fields that fan out to every row, so scope them by the
// union across all rows rather than whichever sibling happens to be first.
const allSubscriptionTypes = computed<string[]>(() => [
  ...new Set(items.value.flatMap((m) => m.accessRules?.subscriptionType ?? [])),
]);
const allRealms = computed<string[]>(() => [
  ...new Set(items.value.flatMap((m) => [m.realm, ...(m.availabilityRealms ?? [])]).filter(Boolean)),
]);

// refdataLinks, grades and areas are manabua-only concepts. Everything else carries plain
// `categories`, and offering the other fields there is what got them filled in by mistake.
const isManabuaMaterial = computed(() => isManabuaSubscription(allSubscriptionTypes.value));

const isItemLoading = computed(() => (itemId: string) => itemLoading.value[itemId] || false);
const isInProgress = computed(() => saveInProgress.value || isStateInProgress.value || isUploadInProgress.value);
const hasTmpFile = computed(() => {
  const result = items.value.some((item) => item.tmpFile && typeof item.tmpFile === 'object');
  console.debug('hasTmpFile', result);
  return result;
});

const availableLanguages = ref([] as Language[]);
getAllRefData('languages').then((languages) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});

const availableSubscriptions = defaultSubscriptions;
const availableMarkets = defaultRealms;
const availableAudiences: Audience[] = ['alone', 'pair', 'group', 'class'];
const availableRoles: string[] = ['employee', 'student', 'guardian'];

const rules = {
  age: (value: number) => (value >= 0 && value <= 99) || 'Age should be between 0 and 99',
  required: (value: unknown) => !!value || 'This field is required',
  requiredMultiple: (value: unknown[]) => value.length > 0 || 'This field is required',
  notEmptyRule: (val: any) => (val || '').length > 0 || 'This field is required',
  trimmedString: (val: string) =>
    !/^\s+|\s+$|\s{2,}/.test(val) || 'Leading, trailing, or multiple whitespaces in a row are not allowed',
};

const addToItem = (key: string, value: any) => {
  items.value = items.value.map((i) => {
    const currentValue = i[key];

    // If the current value is an object and the new value is also an object, merge them
    if (typeof currentValue === 'object' && typeof value === 'object' && !isArray(currentValue)) {
      return { ...i, [key]: { ...currentValue, ...value } };
    }

    // Otherwise, directly assign the value (this works for strings, numbers, etc.)
    return { ...i, [key]: value };
  });
};

const isEmpty = (value: MaterialWithTmpFile) => !value.description || !value.keywords.length;

// Handle the combined selection change
const onCombinedRealm = (index: number, newSelection: string[]) => {
  const filteredNewSelection = newSelection.filter((realm) => availableMarkets.some((market) => market.code === realm));
  combinedRealms.value[index] = filteredNewSelection;
  if (newSelection.length === 0) {
    return;
  }

  // eslint-disable-next-line prefer-destructuring
  items.value[index].realm = filteredNewSelection[0]; // first is always the realm
  items.value[index].availabilityRealms = filteredNewSelection;
};

// Adding or updating an item (both existing and new)
const addOrUpdateItem = (materialWithTmpFile: MaterialWithTmpFile, replace: boolean) => {
  // Extract language from materialWithTmpFile for comparison
  const { language } = materialWithTmpFile;
  // Find the existing object that matches the language
  const existingItemIndex = items.value.findIndex((i) => i.language === language);
  if (existingItemIndex !== -1) {
    // Update the existing item
    if (replace) {
      items.value = [
        ...items.value.slice(0, existingItemIndex),
        {
          id: items.value[existingItemIndex].id,
          state: items.value[existingItemIndex].state,
          ...materialWithTmpFile,
        },
        ...items.value.slice(existingItemIndex + 1),
      ];
      onCombinedRealm(
        existingItemIndex,
        materialWithTmpFile?.realm
          ? [
              materialWithTmpFile.realm,
              ...(materialWithTmpFile?.availabilityRealms.filter((r) => r !== materialWithTmpFile.realm) || []),
            ]
          : [],
      );
      if (materialWithTmpFile.ages) {
        ages.value = materialWithTmpFile.ages;
      }
      if (materialWithTmpFile.accessRules.roles) {
        roles.value = materialWithTmpFile.accessRules.roles;
      }
      properties.value = materialWithTmpFile.properties;
    } else {
      items.value = [
        ...items.value.slice(0, existingItemIndex),
        {
          ...items.value[existingItemIndex],
          tmpFile: materialWithTmpFile.tmpFile,
        },
        ...items.value.slice(existingItemIndex + 1),
      ];
    }
  } else {
    // Add a new item
    items.value.push({
      ...materialWithTmpFile,
      ...{
        originalTitle: originalTitle.value,
        properties: properties.value || materialWithTmpFile.properties,
        accessRules: {
          roles: roles.value || materialWithTmpFile.accessRules.roles,
          subscriptionType: materialWithTmpFile.accessRules.subscriptionType,
        },
        ages: ages.value || materialWithTmpFile.ages,
      },
    });
    const lastIndex = items.value.length - 1;
    onCombinedRealm(
      lastIndex,
      materialWithTmpFile?.realm
        ? [
            materialWithTmpFile.realm,
            ...(materialWithTmpFile?.availabilityRealms.filter((r) => r !== materialWithTmpFile.realm) || []),
          ]
        : [],
    );
  }
};

const onPublish = async () => {
  if (!form.value.validate()) {
    return;
  }
  const res = await Promise.allSettled(
    selected.value.filter((i) => i.state === BaseState.Disabled && i.id).map((i) => toggleMediaState(i)),
  );
  eventBus.$emit('snackbar.open', `Enabled ${res.length} materials`);
};

const onUnpublish = async () => {
  if (!form.value.validate()) {
    return;
  }
  const res = await Promise.allSettled(
    selected.value.filter((i) => i.state === BaseState.Published && i.id).map((i) => toggleMediaState(i)),
  );
  eventBus.$emit('snackbar.open', `Disabled ${res.length} materials`);
};

const onSavePreview = async (materialId: string, file: File) => {
  itemLoading.value[materialId] = true;
  await upload(materialId, file, { subType: 'materialPreview' });
  itemLoading.value[materialId] = false;
  eventBus.$emit('snackbarV2.open', { text: 'Preview Image have been saved successfully!', color: 'success' });
};

const uploadBulk = async (materials: MaterialWithTmpFile[], previewProps?: MaterialPreviewProps) => {
  await Promise.all(
    materials.map(async (m: MaterialWithTmpFile) => {
      if ('tmpFile' in m && m.tmpFile) {
        await uploadMedia(m.id, m.tmpFile, previewProps ?? {});
      }
    }),
  );
};

const onCreateTemplate = () => {
  const template = newMaterial({
    language: undefined,
    ages: ages.value,
    originalTitle: originalTitle.value,
    properties: properties.value,
    accessRules: {
      roles: roles.value,
      subscriptionType: [],
    },
    categories: categories.value,
    areas: areas.value,
    refdataLinks: refdataLinks.value,
    contentLinks: contentLinks.value,
  });
  items.value.push(template);
  const lastIndex = items.value.length - 1;
  onCombinedRealm(lastIndex, []);
};

const onSave = async (previewProps?: MaterialPreviewProps) => {
  // Step 1: Validate the form
  if (!form.value.validate()) return;
  saveInProgress.value = true;
  // Step 2: Separate items into create and update arrays
  const create = items.value.filter((i) => !i.id);
  const update = items.value.filter((i) => i.id);

  // Step 3: Handle bulk creation
  try {
    const createdItems = await createBulk(create);

    // Step 4: Upload files for created items
    await uploadBulk(createdItems, previewProps).then(() => {
      // Step 5: Merge created items into items list
      items.value = [...update, ...createdItems];
      originalItems.value = [
        ...originalItems.value,
        ...createdItems.map((i) => {
          const { tmpFile, ...rest } = i;
          return cloneDeep(rest);
        }),
      ];
    });

    console.debug('Updated items list:', items.value);
  } catch (error) {
    console.error('Error during bulk creation:', error);
    eventBus.$emit('snackbarV2.open', { text: error.message, color: 'error' });
    return;
  }

  // Step 6: Create a diff for updated items (to only update changed fields)
  const changedItems = update.map((i) => {
    const put = patchMaterial(
      originalItems.value.find((oi) => oi.id === i.id),
      i,
    );
    return Object.keys(put).length ? i : { id: i.id, tmpFile: i.tmpFile };
  });

  // Step 7: Perform bulk update for changed items
  try {
    await updateBulk(changedItems);

    // Step 8: Upload files for changed items
    await uploadBulk(changedItems, previewProps).then(() => {
      originalItems.value = items.value.map((i) => {
        const { tmpFile, ...rest } = i;
        return cloneDeep(rest);
      });
    });
  } catch (error) {
    console.error('Error during bulk update:', error);
    eventBus.$emit('snackbarV2.open', { text: error.message, color: 'error' });
    return;
  } finally {
    saveInProgress.value = false;
  }

  const updatedItem = items.value[0];
  console.debug('updatedItem', updatedItem);
  emits('update', updatedItem);
  eventBus.$emit('snackbarV2.open', { text: `Material/s ${id?.value ? 'saved' : 'created'}`, color: 'success' });
  if (props.isCreate) {
    await router.push({
      name: `materials_edit`,
      params: { id: updatedItem?.id },
    });
    return;
  }
  window.location.reload();
  // stay on page for now.
  // router.push('/materials');
};

const propertyOptions = [
  { label: 'Fact book', source: 'forFactBook' },
  { label: 'Chapter Book', source: 'forChapterBook' },
  { label: 'Easy to read', source: 'forEasyToRead' },
  { label: 'Picture book', source: 'forPictureBook' },
  { label: 'Board book (Pekböcker)', source: 'forBoardBook' },
];

const onFileUpload = (files: File[]) => {
  console.debug('files', files);

  if (emptyStateless.value) {
    items.value = items.value.filter((i) => i?.state);
  }

  const unparsable: string[] = [];

  files.forEach((file) => {
    const parsed = parseMaterialFileName(file.name);

    if (!parsed) {
      unparsable.push(file.name);
      return;
    }

    const { title, language, resourceType } = parsed;
    const materialWithTmpFile: MaterialWithTmpFile = {
      ...newMaterial({
        title,
        language,
        realm: guessRealm.value ? languageRealm[language] : undefined,
        resourceType,
        categories: categories.value,
        areas: areas.value,
        refdataLinks: refdataLinks.value,
        contentLinks: contentLinks.value,
      }),
      tmpFile: file,
    };
    addOrUpdateItem(materialWithTmpFile, false);
  });

  // Report every bad name at once, so one typo doesn't silently drop the rest of the batch.
  if (unparsable.length) {
    eventBus.$emit('snackbarV2.open', {
      text: `Filename format doesn't match: ${unparsable.join(', ')}`,
      color: 'error',
    });
  }
};

const onIDClipboardSuccess = () => {
  eventBus.$emit('snackbar.open', 'Id has been copied successfully!');
};

const onMetaDataUpload = (objects: any[]) => {
  objects.forEach((object) => {
    const material = parseMetaData(object);
    console.debug('NewMaterialV1', material);
    addOrUpdateItem(material, true);
  });
};

onMounted(async () => {
  const related = await getOriginalTitleRelated(item?.value);
  related.forEach((material) => {
    addOrUpdateItem(material, false);
    originalItems.value.push(material);
  });
});

return { __sfc: true,emits, props, id, item, router, parseMetaData, newMaterial, patchMaterial, languageRealm, getStateColor, getStateIcon, uploadMedia, toggleMediaState, isStateInProgress, isUploadInProgress, upload, form, isFormValid, selected, expanded, saveInProgress, itemLoading, guessRealm, emptyStateless, originalTitle, contentLinks, refdataLinks, categories, areas, ages, roles, properties, combinedRealms, originalItems, items, headers, selectedIds, selectedSubscriptions, selectedAvailabilityRealms, allSubscriptionTypes, allRealms, isManabuaMaterial, isItemLoading, isInProgress, hasTmpFile, availableLanguages, availableSubscriptions, availableMarkets, availableAudiences, availableRoles, rules, addToItem, isEmpty, onCombinedRealm, addOrUpdateItem, onPublish, onUnpublish, onSavePreview, uploadBulk, onCreateTemplate, onSave, propertyOptions, onFileUpload, onIDClipboardSuccess, onMetaDataUpload, BaseState, ChooseAges, ContentLinks, ContentRefDataTags, RichTextInput, ComboBox, DropFile, ImportButton, PreviewImageDialog, FileInfoBtn, GeneratePreviewDialog, MarketSelect, ActionsMenu, OriginalTitle, RefDataLinks, ContentToCollection }
}

})